/**
 * AdminErrorInboxPage — вграден инбокс за грешки (frontend + backend).
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { AlertOctagon, CheckCircle2, EyeOff, Loader2, RefreshCw, Server, Monitor } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";

const TABS = [
  { key: "new", label: "Нови" },
  { key: "resolved", label: "Решени" },
  { key: "ignored", label: "Игнорирани" },
  { key: "all", label: "Всички" },
];

export default function AdminErrorInboxPage() {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const [tab, setTab] = useState("new");
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [l, s] = await Promise.all([
        axios.get(`${API}/admin/errors`, { params: { status: tab, limit: 100 } }),
        axios.get(`${API}/admin/errors/stats`),
      ]);
      setItems(l.data?.items || []);
      setStats(s.data || null);
    } catch {
      toast.error("Грешка при зареждане на инбокса.");
    } finally {
      setLoading(false);
    }
  }, [API, tab]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try {
      await axios.post(`${API}/admin/errors/${id}/status`, { status });
      load();
    } catch { toast.error("Грешка при промяна на статуса."); }
  };

  const card = isDark ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200";

  return (
    <div className={`min-h-screen ${isDark ? "bg-slate-950 text-slate-100" : "bg-slate-50 text-slate-900"}`}
      data-testid="admin-error-inbox-page">
      <div className="max-w-6xl mx-auto px-4 py-6 space-y-4">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <AlertOctagon className="w-6 h-6 text-rose-500" />
            <h1 className="text-2xl font-extrabold">Инбокс за грешки</h1>
          </div>
          <button onClick={load} className="text-xs font-semibold inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-slate-500/30"
            data-testid="error-inbox-refresh">
            <RefreshCw className="w-3 h-3" /> Опресни
          </button>
        </div>

        {stats && (
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2" data-testid="error-inbox-stats">
            {[
              ["Нови", stats.new, "text-rose-500"],
              ["Решени", stats.resolved, "text-emerald-500"],
              ["Игнорирани", stats.ignored, "text-slate-400"],
              ["Frontend", stats.frontend, "text-sky-500"],
              ["Backend", stats.backend, "text-violet-500"],
              ["24 часа", stats.last_24h, "text-amber-500"],
            ].map(([label, val, cls]) => (
              <div key={label} className={`p-2.5 rounded-xl border ${card} text-center`}>
                <div className="text-[9px] uppercase opacity-55">{label}</div>
                <div className={`text-lg font-bold ${cls}`}>{val}</div>
              </div>
            ))}
          </div>
        )}

        <div className="flex gap-1.5 flex-wrap">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${
                tab === t.key ? "bg-rose-500/15 border-rose-500/50 text-rose-500" : "border-slate-500/30 opacity-70"
              }`}
              data-testid={`error-inbox-tab-${t.key}`}>
              {t.label}
            </button>
          ))}
        </div>

        <div className={`rounded-2xl border ${card} overflow-hidden`} data-testid="error-inbox-table">
          {loading ? (
            <div className="p-4"><Loader2 className="w-5 h-5 animate-spin text-rose-500" /></div>
          ) : items.length === 0 ? (
            <div className="p-4 text-xs opacity-60" data-testid="error-inbox-empty">
              Няма грешки в този изглед. 🎉
            </div>
          ) : (
            <div className="divide-y divide-slate-500/15">
              {items.map((e) => (
                <div key={e.id} className="p-3" data-testid={`error-row-${e.id}`}>
                  <div className="flex items-start gap-2">
                    {e.source === "backend"
                      ? <Server className="w-4 h-4 text-violet-500 mt-0.5 shrink-0" />
                      : <Monitor className="w-4 h-4 text-sky-500 mt-0.5 shrink-0" />}
                    <button onClick={() => setOpen(open === e.id ? null : e.id)} className="flex-1 text-left min-w-0">
                      <div className="text-xs font-bold break-words">{e.message}</div>
                      <div className="text-[10px] opacity-60 mt-0.5 break-all">
                        {e.component || "—"} · {e.url || "—"} · {e.user_email || "анонимен"} · ×{e.count} ·{" "}
                        {new Date(e.last_seen).toLocaleString("bg-BG")}
                      </div>
                    </button>
                    <div className="flex items-center gap-1 shrink-0">
                      <button onClick={() => setStatus(e.id, "resolved")}
                        className="p-1.5 rounded-md text-emerald-500 hover:bg-emerald-500/10"
                        title="Решен" data-testid={`error-resolve-${e.id}`}>
                        <CheckCircle2 className="w-4 h-4" />
                      </button>
                      <button onClick={() => setStatus(e.id, "ignored")}
                        className="p-1.5 rounded-md text-slate-400 hover:bg-slate-500/10"
                        title="Игнорирай" data-testid={`error-ignore-${e.id}`}>
                        <EyeOff className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  {open === e.id && e.stack && (
                    <pre className={`mt-2 p-2 rounded-lg text-[9px] overflow-x-auto whitespace-pre-wrap ${
                      isDark ? "bg-black/40" : "bg-slate-100"}`} data-testid={`error-stack-${e.id}`}>
                      {e.stack}
                    </pre>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
